import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChatComposer } from "@/components/looseend/ChatComposer";
import { EmptyConversationState } from "@/components/looseend/EmptyConversationState";
import { PeeSummaryPanel } from "@/components/looseend/PeeSummaryPanel";
import { toast } from "sonner";
import { useHostedChatActions } from "@/lib/chat/product/hostedChat";
import { useAppState } from "@/lib/looseend/store";
import { analytics } from "@/lib/analytics";
import type { MessageAttachment } from "@/lib/looseend/types";

export const Route = createFileRoute("/app/")({
  head: () => ({
    meta: [
      { title: "L∞seEnd™ — Personal Execution Engine" },
      {
        name: "description",
        content:
          "Tell LooseEnd what's hanging over you — unfinished, overdue, or waiting on someone else — and track it to verified resolution. No account needed.",
      },
      { property: "og:title", content: "L∞seEnd™ — Personal Execution Engine" },
      { property: "og:description", content: "What's hanging over you?" },
    ],
  }),
  component: AppIndex,
});

/**
 * Conversational empty state: the first thing you see when you open the app.
 * Guest-first: a visitor types here immediately; the guest identity, the
 * conversation and the first turn are all created by the backend on submit.
 */
function AppIndex() {
  const navigate = useNavigate();
  const chat = useHostedChatActions();
  const isGuest = useAppState().user?.isGuest === true;

  /**
   * The conversation and its first turn are created by the backend. We navigate
   * only once the authority has assigned the conversation id, so the URL can
   * never point at a conversation that does not durably exist.
   */
  const send = (text: string, _attachments?: MessageAttachment[]) => {
    void _attachments;
    analytics.track("conversation_started", { interface_surface: "home" });
    analytics.track("loop_capture_started", { interface_surface: "home" });
    void (async () => {
      const created = await chat.startConversation();
      if (!created.ok) {
        toast.error(created.message);
        return;
      }
      const conversationId = created.value;
      await navigate({ to: "/app/chat/$conversationId", params: { conversationId } });
      const turn = await chat.sendTurn(conversationId, text);
      if (!turn.ok) {
        toast.error(turn.message);
        return;
      }
      analytics.track("loop_preview_generated", { interface_surface: "home", success: true });
    })();
  };

  /**
   * Progressive disclosure — PEE™ is introduced after the user has product
   * context. The PEE onboarding card no longer interrupts first use; the
   * returning-user summary (which only renders over a non-zero hosted Loop
   * tally) remains.
   */
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-0 flex-1 flex-col justify-center gap-4 overflow-y-auto px-4 py-6 sm:px-6">
        <PeeSummaryPanel />
        <EmptyConversationState onSuggestion={send} />
      </div>
      <div className="shrink-0 bg-background px-4 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6">
        <ChatComposer onSend={send} disabled={chat.busy} />
        {isGuest ? (
          <p
            className="content-column mt-2 text-center text-xs text-muted-foreground"
            data-testid="home-guest-note"
          >
            Start a conversation now.{" "}
            <Link
              to="/auth"
              search={{ redirect: "/app" }}
              className="font-medium text-foreground underline-offset-2 hover:underline"
            >
              Sign in
            </Link>{" "}
            anytime to save and access your history.
          </p>
        ) : null}
      </div>
    </div>
  );
}
